import React, { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { filterCategoryTypesAtom } from '@src/atoms/page';
import { BaseChart, Retry } from '@components/common';
import { buildDurationTreeOption, buildStackedOption } from './utils';
import { NETWORK_BASED_CATEGORY_EXCLUDES } from '@src/utils/presets/constants';
import { useChartInstance } from '@src/components/common/BaseChart';
import { useDurationDistribution } from './hooks/useDurationDistribution';
import { ChartWrapper } from '../styled';
import useDurationTimeseries from './hooks/useDurationTimeseries';
import { StackWrapper } from './styled';
import WidgetCardWrapper from '../../common/WidgetCardWrapper';
import Extra from './components/Extra';
import { useWidget } from '../../state/dashboard';
import { useValidateParams } from './hooks/useValidateParams';
import WidgetWarning from '../../common/WidgetWarning';

export function NetworkDurationWidget({ wid }) {
  const widgetHandler = useWidget({ wid });
  const [showTreeMap, setShowTreeMap] = useState(false);
  const filterCategories = useAtomValue(filterCategoryTypesAtom);

  const isNAFilter = filterCategories.some((category: any) => {
    return NETWORK_BASED_CATEGORY_EXCLUDES.includes(category);
  });

  const { isIntervalValid, message } = useValidateParams({});

  const getWidgets = () => {
    if (isNAFilter) {
      return <WidgetWarning message="Not Applicable" />;
    } else if (!isIntervalValid) {
      return <WidgetWarning message={message} />;
    } else {
      return <StackedDuration wid={wid} showTreeMap={showTreeMap} />;
    }
  };

  return (
    <WidgetCardWrapper
      wid={wid}
      title="Network: Duration"
      style={{ gridRow: 'span 14' }}
      extra={
        <Extra
          showTreeMap={showTreeMap}
          setShowTreeMap={setShowTreeMap}
          widgetHandler={widgetHandler}
          showOptions={isIntervalValid && !isNAFilter}
        />
      }
    >
      <StackWrapper>{getWidgets()}</StackWrapper>
    </WidgetCardWrapper>
  );
}

function StackedDuration({ wid, showTreeMap }) {
  const { instance, chartInstanceRefCb } = useChartInstance({});

  const { data: chartData, ...status } = useDurationTimeseries({ wid });

  const { data: treeData, ...treeStatus } = useDurationDistribution({ wid, enabled: showTreeMap });

  useEffect(() => {
    if (instance !== null) {
      let option: any = {};
      if (showTreeMap && !treeStatus.isLoading) {
        option = buildDurationTreeOption(treeData);
      } else if (!status.isLoading) {
        option = buildStackedOption(chartData) as any;
      }
      instance.setOption(option, { notMerge: true });
    }
  }, [instance, chartData, status.isLoading, showTreeMap, treeStatus.isLoading]);

  const retryStatus = showTreeMap ? treeStatus : status;

  return (
    <ChartWrapper>
      <Retry {...retryStatus} progress>
        <BaseChart
          key="network-duration-chart"
          chartId="network-duration-chart"
          chartInstanceRefCb={chartInstanceRefCb}
          style={{ height: '100%', minHeight: 210 }}
        />
      </Retry>
    </ChartWrapper>
  );
}

export default NetworkDurationWidget;
